import {
  LAST_STEP_DONE,
  VALUELESS_OPS,
  WORKFLOW_CATALOGUE,
  WORKFLOW_OPS,
  WORKFLOW_REPEATS,
  type WorkflowActionDef,
  type WorkflowCondition,
  type WorkflowEdge,
  type WorkflowFieldDef,
  type WorkflowGraph,
  type WorkflowInput,
  type WorkflowNode,
  type WorkflowSubject,
  type WorkflowTarget,
  type WorkflowTrigger,
  type WorkflowView,
  type WorkflowWait,
} from "@dentacore/shared-types";

/**
 * **The editor's model of a workflow** (STORY-224, WG-D4). Pure functions over
 * a `WorkflowGraph`, so the board only draws and the rules live in one place.
 *
 * The graph is a tree hanging from the Start box, as the server insists
 * (`apps/api/src/workflows/graph.ts`): every other box has exactly one arrow in;
 * a step or a wait at most one out; a fork exactly two, *if* and *otherwise*.
 * Every change here keeps that shape, so the only thing the server can still
 * refuse is a box left half filled in — and it says which in a sentence.
 */

// ── Layout constants (the prototype's) ──────────────────────────────────────

/** A box's width. Every box is this wide, so a column is too. */
export const BOX_W = 236;
/** An arrow's width between two boxes — room for its condition chip. */
export const GAP = 176;
/** The gap between a box and the dashed *Add* box after it. */
export const ADD_GAP = 96;
/** The *Add* box's size. */
export const ADD_W = 156;
export const ADD_H = 112;
/** Space round the line. */
export const PAD = 28;
/** Between one row and the next when a fork splits. */
export const ROW_GAP = 56;
/** A box not yet measured is drawn at about this height. */
export const GUESS_H = 110;

// ── Ids ─────────────────────────────────────────────────────────────────────

/** A short id unique within one workflow. The server only checks uniqueness. */
export function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-3)}`;
}

// ── Reading the graph ───────────────────────────────────────────────────────

export function startOf(graph: WorkflowGraph): WorkflowNode | undefined {
  return graph.nodes.find((n) => n.kind === "start");
}

export function nodeById(graph: WorkflowGraph, id: string): WorkflowNode | undefined {
  return graph.nodes.find((n) => n.id === id);
}

export function outOf(graph: WorkflowGraph, id: string): WorkflowEdge[] {
  return graph.edges.filter((e) => e.from === id);
}

export function intoNode(graph: WorkflowGraph, id: string): WorkflowEdge | undefined {
  return graph.edges.find((e) => e.to === id);
}

/** Every box before `id` on its path from Start, nearest last (the server's `pathTo`). */
export function pathTo(graph: WorkflowGraph, id: string): WorkflowNode[] {
  const path: WorkflowNode[] = [];
  let at = id;
  for (let guard = 0; guard <= graph.nodes.length; guard++) {
    const into = intoNode(graph, at);
    if (into === undefined) break;
    const from = nodeById(graph, into.from);
    if (from === undefined) break;
    path.unshift(from);
    at = from.id;
  }
  return path;
}

/** `id` and every box hanging below it. */
export function subtree(graph: WorkflowGraph, id: string): Set<string> {
  const ids = new Set<string>([id]);
  const queue = [id];
  while (queue.length > 0) {
    const at = queue.shift() as string;
    for (const edge of outOf(graph, at)) {
      if (ids.has(edge.to)) continue;
      ids.add(edge.to);
      queue.push(edge.to);
    }
  }
  return ids;
}

/**
 * The boxes in reading order — Start, then depth first, the *if* branch before
 * *otherwise*. Step numbers come from this order.
 */
export function readingOrder(graph: WorkflowGraph): WorkflowNode[] {
  const start = startOf(graph);
  if (start === undefined) return [];
  const order: WorkflowNode[] = [];
  const seen = new Set<string>();
  const visit = (id: string): void => {
    if (seen.has(id)) return;
    seen.add(id);
    const node = nodeById(graph, id);
    if (node === undefined) return;
    order.push(node);
    const outs = outOf(graph, id).sort((a, b) => branchRank(a) - branchRank(b));
    for (const edge of outs) visit(edge.to);
  };
  visit(start.id);
  return order;
}

const branchRank = (edge: WorkflowEdge): number => (edge.branch === "no" ? 1 : 0);

/** Each box's number on the board: 1, 2, 3… in reading order, Start unnumbered. */
export function stepNumbers(graph: WorkflowGraph): Map<string, number> {
  const numbers = new Map<string, number>();
  let n = 0;
  for (const node of readingOrder(graph)) if (node.kind !== "start") numbers.set(node.id, ++n);
  return numbers;
}

/** Where a new box can go: a box with a free way out. */
export interface OpenEnd {
  from: string;
  branch?: "yes" | "no";
}

export function openEnds(graph: WorkflowGraph): OpenEnd[] {
  const ends: OpenEnd[] = [];
  for (const node of readingOrder(graph)) {
    const outs = outOf(graph, node.id);
    if (node.kind === "fork") {
      if (!outs.some((e) => e.branch === "yes")) ends.push({ from: node.id, branch: "yes" });
      if (!outs.some((e) => e.branch === "no")) ends.push({ from: node.id, branch: "no" });
    } else if (outs.length === 0) {
      ends.push({ from: node.id });
    }
  }
  return ends;
}

// ── The catalogue, as the editor needs it ───────────────────────────────────

export function actionDef(
  subject: WorkflowSubject,
  key: string | undefined,
): WorkflowActionDef | null {
  if (key === undefined) return null;
  return WORKFLOW_CATALOGUE[subject].actions.find((a) => a.key === key) ?? null;
}

/** The record type a step acts on, or null when its target names nothing (the server's `targetSubject`). */
export function targetSubject(
  graph: WorkflowGraph,
  subject: WorkflowSubject,
  target: WorkflowTarget | undefined,
  guard = 0,
): WorkflowSubject | null {
  if (target === undefined || target === "self") return subject;
  if (guard > graph.nodes.length) return null;
  if (target.startsWith("link:")) {
    const key = target.slice(5);
    return WORKFLOW_CATALOGUE[subject].links.find((l) => l.key === key)?.to ?? null;
  }
  const made = nodeById(graph, target.slice(5));
  if (made?.action === undefined) return null;
  const from = targetSubject(graph, subject, made.target, guard + 1);
  if (from === null) return null;
  return actionDef(from, made.action)?.makes ?? null;
}

/** A step's action, read against the record its target is. */
export function nodeAction(
  graph: WorkflowGraph,
  subject: WorkflowSubject,
  node: WorkflowNode,
): WorkflowActionDef | null {
  const on = targetSubject(graph, subject, node.target);
  return on === null ? null : actionDef(on, node.action);
}

/** What a step may act on: the record, its links, and anything a step before it made (WG-D8). */
export function targetOptions(
  graph: WorkflowGraph,
  subject: WorkflowSubject,
  nodeId: string,
): { value: WorkflowTarget; label: string }[] {
  const def = WORKFLOW_CATALOGUE[subject];
  const options: { value: WorkflowTarget; label: string }[] = [
    { value: "self", label: `This ${def.title.toLowerCase()}` },
    ...def.links.map((l) => ({ value: `link:${l.key}` as WorkflowTarget, label: l.label })),
  ];
  const numbers = stepNumbers(graph);
  for (const before of pathTo(graph, nodeId)) {
    if (before.kind !== "action") continue;
    const on = targetSubject(graph, subject, before.target);
    const made = on === null ? null : (actionDef(on, before.action)?.makes ?? null);
    if (made === null) continue;
    options.push({
      value: `made:${before.id}`,
      label: `The ${WORKFLOW_CATALOGUE[made].title.toLowerCase()} step ${String(numbers.get(before.id) ?? "?")} made`,
    });
  }
  return options;
}

/**
 * Fields a condition may read: the record's own, then those of each record it
 * links to as `link.key` (the server's `fieldDef` follows one link). A key the
 * record already carries as its own — a booking's `patient.balance` — is not
 * offered twice.
 */
export function conditionFields(
  subject: WorkflowSubject,
): { key: string; label: string; def: WorkflowFieldDef }[] {
  const def = WORKFLOW_CATALOGUE[subject];
  const fields = def.fields.map((f) => ({ key: f.key, label: f.label, def: f }));
  const seen = new Set(fields.map((f) => f.key));
  for (const link of def.links) {
    for (const f of WORKFLOW_CATALOGUE[link.to].fields) {
      const key = `${link.key}.${f.key}`;
      if (seen.has(key)) continue;
      seen.add(key);
      fields.push({ key, label: `${link.label} · ${f.label.toLowerCase()}`, def: f });
    }
  }
  return fields;
}

export function findField(subject: WorkflowSubject, key: string): WorkflowFieldDef | null {
  return conditionFields(subject).find((f) => f.key === key)?.def ?? null;
}

/** A sensible first condition on a record: its first field, its first rule, its first value. */
export function defaultCondition(subject: WorkflowSubject): WorkflowCondition {
  const first = conditionFields(subject)[0];
  if (first === undefined) return { field: LAST_STEP_DONE, op: "yes" };
  return conditionFor(first.key, first.def);
}

/** A field just picked: its first rule and a value that fits. */
export function conditionFor(key: string, def: WorkflowFieldDef): WorkflowCondition {
  const op = WORKFLOW_OPS[def.type][0]?.op ?? "is";
  if (VALUELESS_OPS.includes(op)) return { field: key, op };
  if (def.type === "enum") return { field: key, op, value: def.options?.[0]?.value ?? "" };
  if (def.type === "number" || def.type === "money") return { field: key, op, value: 0 };
  return { field: key, op, value: "" };
}

// ── Making a new workflow ───────────────────────────────────────────────────

export function emptyDraft(subject: WorkflowSubject = "booking"): WorkflowInput {
  return {
    name: "New workflow",
    subject,
    trigger: { kind: "ring" },
    enabled: true,
    allBranches: true,
    branchIds: [],
    roleIds: [],
    runAsRoleId: null,
    graph: { nodes: [{ id: newId("start"), kind: "start", x: PAD, y: 160 }], edges: [] },
  };
}

/** What the server keeps, out of what it sends back. */
export function inputOf(view: WorkflowView): WorkflowInput {
  return {
    name: view.name,
    subject: view.subject,
    trigger: view.trigger,
    enabled: view.enabled,
    allBranches: view.allBranches,
    branchIds: [...view.branchIds],
    roleIds: [...view.roleIds],
    runAsRoleId: view.runAsRoleId,
    graph: {
      nodes: view.graph.nodes.map((n) => ({ ...n })),
      edges: view.graph.edges.map((e) => ({ ...e })),
    },
  };
}

/** A trigger that fits a record type and a way of starting. */
export function triggerFor(
  kind: WorkflowTrigger["kind"],
  subject: WorkflowSubject,
): WorkflowTrigger {
  if (kind === "ring") return { kind: "ring" };
  if (kind === "event") return { kind: "event", on: "changed", field: null, value: null };
  const windows = WORKFLOW_CATALOGUE[subject].windows ?? [];
  return { kind: "schedule", repeat: "day", at: "19:00", window: windows[0]?.value ?? null };
}

// ── Changing the graph (each returns a new graph) ───────────────────────────

/** The first thing a new step does: the record's first action that runs on its own. */
function firstAction(subject: WorkflowSubject): string | undefined {
  const actions = WORKFLOW_CATALOGUE[subject].actions;
  return (actions.find((a) => a.kind === "auto" && a.params === undefined) ?? actions[0])?.key;
}

export type NewBoxKind = "action" | "fork" | "wait";

/**
 * A new box on a free way out of `end.from`, **an arrow's width after it**
 * (WF-D8): nothing already placed moves. A box on a fork's *otherwise* lands a
 * row below.
 */
export function addBox(
  graph: WorkflowGraph,
  subject: WorkflowSubject,
  end: OpenEnd,
  kind: NewBoxKind,
  heights: ReadonlyMap<string, number>,
): { graph: WorkflowGraph; id: string } {
  const from = nodeById(graph, end.from);
  if (from === undefined) return { graph, id: "" };
  const id = newId(kind === "action" ? "step" : kind);
  const fromH = heights.get(from.id) ?? GUESS_H;
  const x = from.x + BOX_W + GAP;
  const y = end.branch === "no" ? from.y + fromH + ROW_GAP : from.y;
  const node: WorkflowNode =
    kind === "action"
      ? { id, kind, x, y, action: firstAction(subject), target: "self" }
      : kind === "fork"
        ? { id, kind, x, y, condition: defaultCondition(subject) }
        : { id, kind, x, y, wait: { kind: "for", amount: 1, unit: "days" } };
  const edge: WorkflowEdge = {
    id: newId("arrow"),
    from: from.id,
    to: id,
    ...(end.branch === undefined ? {} : { branch: end.branch }),
    condition: null,
  };
  return { graph: { nodes: [...graph.nodes, node], edges: [...graph.edges, edge] }, id };
}

/** What removing a box would also remove: a fork takes its *otherwise* branch with it. */
export function removalCost(graph: WorkflowGraph, id: string): number {
  const node = nodeById(graph, id);
  if (node?.kind !== "fork") return 0;
  const no = outOf(graph, id).find((e) => e.branch === "no");
  return no === undefined ? 0 : subtree(graph, no.to).size;
}

/**
 * Take a box out and join what came after it to what came before (WF-D8 — no
 * other box moves). A fork keeps its *if* branch and drops *otherwise*, whole.
 */
export function removeBox(graph: WorkflowGraph, id: string): WorkflowGraph {
  const node = nodeById(graph, id);
  if (node === undefined || node.kind === "start") return graph;
  const into = intoNode(graph, id);
  const outs = outOf(graph, id);
  const drop = new Set<string>([id]);
  let next: string | undefined;
  if (node.kind === "fork") {
    const no = outs.find((e) => e.branch === "no");
    if (no !== undefined) for (const gone of subtree(graph, no.to)) drop.add(gone);
    next = outs.find((e) => e.branch === "yes")?.to;
  } else {
    next = outs[0]?.to;
  }
  const edges = graph.edges
    .filter((e) => !drop.has(e.from) && !drop.has(e.to))
    .filter((e) => e.id !== into?.id);
  // The arrow that came in now leads to what came next, keeping its branch and condition.
  if (into !== undefined && next !== undefined) edges.push({ ...into, to: next });
  return sanitize({ nodes: graph.nodes.filter((n) => !drop.has(n.id)), edges });
}

/** Only steps and waits trade places; a fork carries branches and stays put. */
const swappable = (node: WorkflowNode | undefined): boolean =>
  node !== undefined && (node.kind === "action" || node.kind === "wait");

/** The box ‹ or › would trade places with, if any (WF-D11). */
export function swapPartner(
  graph: WorkflowGraph,
  id: string,
  direction: "left" | "right",
): WorkflowNode | undefined {
  const node = nodeById(graph, id);
  if (!swappable(node)) return undefined;
  if (direction === "left") {
    const into = intoNode(graph, id);
    const before = into === undefined ? undefined : nodeById(graph, into.from);
    return swappable(before) ? before : undefined;
  }
  const outs = outOf(graph, id);
  const after = outs.length === 1 ? nodeById(graph, outs[0]?.to ?? "") : undefined;
  return swappable(after) ? after : undefined;
}

/**
 * ‹ › — trade a box with its neighbour in the line (WF-D11). **Each box takes
 * its incoming condition with it**; the arrows' *if* / *otherwise* stay where
 * they are; the two boxes trade places on the canvas so the arrows stay
 * readable. Nothing else moves.
 */
export function swapBoxes(
  graph: WorkflowGraph,
  id: string,
  direction: "left" | "right",
): WorkflowGraph {
  const partner = swapPartner(graph, id, direction);
  if (partner === undefined) return graph;
  const [first, second] = direction === "left" ? [partner.id, id] : [id, partner.id];
  // first → second, in that order on the line.
  const intoFirst = intoNode(graph, first);
  const between = intoNode(graph, second);
  const afterSecond = outOf(graph, second)[0];
  if (intoFirst === undefined || between === undefined) return graph;
  const edges = graph.edges.map((e): WorkflowEdge => {
    // parent → second, carrying second's own condition (and the parent's branch).
    if (e.id === intoFirst.id) return { ...e, to: second, condition: between.condition };
    // second → first, carrying first's condition.
    if (e.id === between.id)
      return { id: e.id, from: second, to: first, condition: intoFirst.condition };
    // first → whatever followed second.
    if (afterSecond !== undefined && e.id === afterSecond.id) return { ...e, from: first };
    return e;
  });
  const a = nodeById(graph, first);
  const b = nodeById(graph, second);
  const nodes = graph.nodes.map((n): WorkflowNode => {
    if (a !== undefined && b !== undefined && n.id === a.id) return { ...n, x: b.x, y: b.y };
    if (a !== undefined && b !== undefined && n.id === b.id) return { ...n, x: a.x, y: a.y };
    return n;
  });
  return sanitize({ nodes, edges });
}

/**
 * Keep the arrows legal after a change: an arrow out of a fork carries no
 * condition, *last step was done* is never on the first arrow, and a step
 * aimed at a record a removed step made goes back to the start record.
 */
export function sanitize(graph: WorkflowGraph): WorkflowGraph {
  const kinds = new Map(graph.nodes.map((n) => [n.id, n.kind]));
  const ids = new Set(graph.nodes.map((n) => n.id));
  const edges = graph.edges.map((e): WorkflowEdge => {
    const from = kinds.get(e.from);
    if (from === "fork" && e.condition !== null) return { ...e, condition: null };
    // Only an arrow out of a fork is *if* or *otherwise*.
    const plain: WorkflowEdge =
      from !== "fork" && e.branch !== undefined
        ? { id: e.id, from: e.from, to: e.to, condition: e.condition }
        : e;
    if (from === "start" && plain.condition?.field === LAST_STEP_DONE)
      return { ...plain, condition: null };
    return plain;
  });
  const nodes = graph.nodes.map((n): WorkflowNode =>
    n.target?.startsWith("made:") === true && !ids.has(n.target.slice(5))
      ? { ...n, target: "self" }
      : n,
  );
  return { nodes, edges };
}

export function updateNode(
  graph: WorkflowGraph,
  id: string,
  change: (node: WorkflowNode) => WorkflowNode,
): WorkflowGraph {
  return { ...graph, nodes: graph.nodes.map((n) => (n.id === id ? change(n) : n)) };
}

export function updateEdge(
  graph: WorkflowGraph,
  id: string,
  change: (edge: WorkflowEdge) => WorkflowEdge,
): WorkflowGraph {
  return { ...graph, edges: graph.edges.map((e) => (e.id === id ? change(e) : e)) };
}

/** A step given a new action: it keeps only the parameters the new action still has. */
export function withAction(
  graph: WorkflowGraph,
  subject: WorkflowSubject,
  node: WorkflowNode,
  action: string,
): WorkflowNode {
  const on = targetSubject(graph, subject, node.target);
  const def = on === null ? null : actionDef(on, action);
  const keys = new Set((def?.params ?? []).map((p) => p.key));
  const params = Object.fromEntries(
    Object.entries(node.params ?? {}).filter(([key]) => keys.has(key)),
  );
  return { ...node, action, params };
}

/** A new record type: only the Start box survives, since every step and condition was about the old one. */
export function clearSteps(graph: WorkflowGraph): WorkflowGraph {
  const start = startOf(graph);
  return { nodes: start === undefined ? [] : [start], edges: [] };
}

// ── Tidy up (WF-D8, WF-D9) ──────────────────────────────────────────────────

/**
 * **Line the boxes up** — only *Tidy up* and *Save* do this. One horizontal
 * line, an arrow's width between boxes, sitting across the canvas's middle;
 * the line scrolls sideways rather than wrapping. A fork's *if* branch carries
 * on along the line and its *otherwise* takes the next free row below, so each
 * branch is a line of its own. The order is the arrows' order — never where
 * the boxes happened to sit (WF-D11).
 */
export function tidy(
  graph: WorkflowGraph,
  heights: ReadonlyMap<string, number>,
  viewportHeight: number,
): WorkflowGraph {
  const start = startOf(graph);
  if (start === undefined) return graph;
  const place = new Map<string, { col: number; row: number }>();
  let lastRow = 0;
  const visit = (id: string, col: number, row: number): void => {
    if (place.has(id)) return;
    place.set(id, { col, row });
    lastRow = Math.max(lastRow, row);
    const node = nodeById(graph, id);
    const outs = outOf(graph, id);
    if (node?.kind === "fork") {
      const yes = outs.find((e) => e.branch === "yes");
      const no = outs.find((e) => e.branch === "no");
      if (yes !== undefined) visit(yes.to, col + 1, row);
      // The *otherwise* branch — or the room for its first box — takes a row of its own.
      const below = lastRow + 1;
      lastRow = below;
      if (no !== undefined) visit(no.to, col + 1, below);
    } else {
      for (const edge of outs) visit(edge.to, col + 1, row);
    }
  };
  visit(start.id, 0, 0);

  const tallest = Math.max(ADD_H, ...graph.nodes.map((n) => heights.get(n.id) ?? GUESS_H));
  const rowH = tallest + ROW_GAP;
  const rows = lastRow + 1;
  const firstCentre = Math.max(PAD + tallest / 2 + 24, (viewportHeight - (rows - 1) * rowH) / 2);
  const nodes = graph.nodes.map((n): WorkflowNode => {
    const at = place.get(n.id);
    // A box nothing reaches keeps its place; the server will say it is loose.
    if (at === undefined) return n;
    const h = heights.get(n.id) ?? GUESS_H;
    return {
      ...n,
      x: PAD + at.col * (BOX_W + GAP),
      y: Math.round(firstCentre + at.row * rowH - h / 2),
    };
  });
  return { ...graph, nodes };
}

/** All boxes on one spot — a workflow made without the editor. It wants tidying on open. */
export function needsTidy(graph: WorkflowGraph): boolean {
  if (graph.nodes.length < 2) return false;
  const first = graph.nodes[0];
  return graph.nodes.every((n) => n.x === first?.x && n.y === first.y);
}

// ── Words ───────────────────────────────────────────────────────────────────

/** "3 steps · 2 someone does · 1 runs on its own · 1 fork · 1 wait" (WF-D10). Never the steps. */
export function countLine(input: Pick<WorkflowInput, "graph" | "subject">): string {
  const { graph, subject } = input;
  let steps = 0;
  let person = 0;
  let forks = 0;
  let waits = 0;
  for (const node of graph.nodes) {
    if (node.kind === "action") {
      steps++;
      if (nodeAction(graph, subject, node)?.kind === "person") person++;
    } else if (node.kind === "fork") forks++;
    else if (node.kind === "wait") waits++;
  }
  const auto = steps - person;
  const parts = [`${String(steps)} step${steps === 1 ? "" : "s"}`];
  if (person > 0) parts.push(`${String(person)} someone does`);
  if (auto > 0)
    parts.push(
      `${String(auto)} run${auto === 1 ? "s" : ""} on ${auto === 1 ? "its" : "their"} own`,
    );
  if (forks > 0) parts.push(`${String(forks)} fork${forks === 1 ? "" : "s"}`);
  if (waits > 0) parts.push(`${String(waits)} wait${waits === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

const EVENT_WORDS: Record<"created" | "changed" | "deleted" | "any", string> = {
  created: "is added",
  changed: "changes",
  deleted: "is deleted",
  any: "is added, changed or deleted",
};

export const EVENT_OPTIONS: { value: "created" | "changed" | "deleted" | "any"; label: string }[] =
  [
    { value: "created", label: "Is added" },
    { value: "changed", label: "Changes" },
    { value: "deleted", label: "Is deleted" },
    { value: "any", label: "Any of these" },
  ];

/** A value as a person reads it: an enum's words, money in the clinic's currency. */
export function valueWords(
  def: WorkflowFieldDef | null,
  value: WorkflowCondition["value"],
  money: (minor: number) => string,
): string {
  if (value === undefined || value === null) return "";
  if (def?.type === "enum")
    return def.options?.find((o) => o.value === value)?.label ?? String(value);
  if (def?.type === "money") return money(Number(value));
  if (def?.type === "bool") return value === "true" || value === 1 ? "yes" : "no";
  return String(value);
}

/** "From the quick ring, on a booking" · "When a booking's status becomes No-show" · "Every day at 19:00". */
export function triggerSummary(
  subject: WorkflowSubject,
  trigger: WorkflowTrigger,
  money: (minor: number) => string = String,
): string {
  const def = WORKFLOW_CATALOGUE[subject];
  if (trigger.kind === "ring") return `From the quick ring, on ${def.label}`;
  if (trigger.kind === "event") {
    if (trigger.field !== null && trigger.field !== undefined) {
      const field = def.fields.find((f) => f.key === trigger.field) ?? null;
      const label = field?.label.toLowerCase() ?? trigger.field;
      return `When ${def.label}’s ${label} becomes ${valueWords(field, trigger.value, money)}`;
    }
    return `When ${def.label} ${EVENT_WORDS[trigger.on]}`;
  }
  const repeat = WORKFLOW_REPEATS.find((r) => r.value === trigger.repeat)?.label ?? trigger.repeat;
  const window = def.windows?.find((w) => w.value === trigger.window)?.label;
  return `${repeat} at ${trigger.at}${window === undefined ? "" : ` · ${window.toLowerCase()}`}`;
}

/** "Status is Checked in" · "Owes is above $50.00" · "Only if the last step was done". */
export function conditionSummary(
  subject: WorkflowSubject,
  condition: WorkflowCondition | null,
  money: (minor: number) => string = String,
): string {
  if (condition === null) return "Always";
  if (condition.field === LAST_STEP_DONE) return "Only if the last step was done";
  const field = conditionFields(subject).find((f) => f.key === condition.field);
  if (field === undefined) return "Pick a condition";
  const rule =
    WORKFLOW_OPS[field.def.type].find((o) => o.op === condition.op)?.label ?? condition.op;
  if (VALUELESS_OPS.includes(condition.op)) return `${field.label} ${rule}`;
  if (condition.op === "withinDays")
    return `${field.label} is within the next ${String(condition.value ?? "…")} days`;
  if (condition.op === "olderThanDays")
    return `${field.label} is more than ${String(condition.value ?? "…")} days ago`;
  return `${field.label} ${rule} ${valueWords(field.def, condition.value, money)}`;
}

export function waitSummary(
  subject: WorkflowSubject,
  wait: WorkflowWait | undefined,
  money: (minor: number) => string = String,
): string {
  if (wait === undefined) return "Say what it waits for";
  if (wait.kind === "for") {
    const unit = wait.amount === 1 ? wait.unit.slice(0, -1) : wait.unit;
    return `${String(wait.amount)} ${unit} later`;
  }
  if (wait.kind === "until") return `Until ${wait.at} ${wait.day}`;
  const unit = wait.giveUp.amount === 1 ? wait.giveUp.unit.slice(0, -1) : wait.giveUp.unit;
  return `Until ${conditionSummary(subject, wait.condition, money).replace(/^./, (c) => c.toLowerCase())} · gives up after ${String(wait.giveUp.amount)} ${unit}`;
}
